<script lang="ts">
  import {
    SUPPORTED_IMAGE_TYPES,
    imageFromDataTransfer,
    validateImageFile,
  } from '$lib/image/intake'

  interface Props {
    onselect: (file: File) => void
    onerror: (message: string) => void
  }

  const { onselect, onerror }: Props = $props()

  let dragging = $state(false)
  let loadingSample = $state('')
  let input: HTMLInputElement

  const accept = SUPPORTED_IMAGE_TYPES.join(',')

  /** Demo drawings shipped with the app, for trying it without finding a file. */
  const SAMPLES = [
    { file: 'rings.png', label: 'Rings' },
    { file: 'spiral.png', label: 'Spiral' },
    { file: 'leaf.png', label: 'Leaf' },
  ]

  async function useSample(name: string) {
    loadingSample = name
    try {
      // BASE_URL, not a bare path: the app may be served from a sub-path
      // rather than a domain root.
      const response = await fetch(`${import.meta.env.BASE_URL}samples/${name}`)
      if (!response.ok) throw new Error(`Sample unavailable (${response.status})`)
      const blob = await response.blob()
      onselect(new File([blob], name, { type: 'image/png' }))
    } catch (cause) {
      onerror(cause instanceof Error ? cause.message : String(cause))
    } finally {
      loadingSample = ''
    }
  }

  function accepted(file: File | null) {
    if (!file) {
      onerror('No image was found in what you dropped.')
      return
    }
    const result = validateImageFile(file)
    if (!result.ok) {
      onerror(result.message)
      return
    }
    onselect(result.file)
  }

  function onDrop(event: DragEvent) {
    event.preventDefault()
    dragging = false
    accepted(imageFromDataTransfer(event.dataTransfer) as File | null)
  }

  function onDragOver(event: DragEvent) {
    event.preventDefault()
    dragging = true
  }

  function onPick(event: Event) {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0] ?? null
    // Reset so re-picking the same file still fires a change event.
    target.value = ''
    accepted(file)
  }
</script>

<svelte:window
  onpaste={(event: ClipboardEvent) => {
    const file = imageFromDataTransfer(event.clipboardData) as File | null
    if (file) accepted(file)
  }}
/>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="zone"
  class:dragging
  data-testid="upload-zone"
  ondrop={onDrop}
  ondragover={onDragOver}
  ondragleave={() => (dragging = false)}
>
  <p class="title">Drop a line art image here</p>
  <p class="sub">or paste from the clipboard</p>
  <button type="button" data-testid="upload-button" onclick={() => input.click()}>
    Choose an image
  </button>
  <input
    bind:this={input}
    type="file"
    {accept}
    data-testid="file-input"
    onchange={onPick}
    hidden
  />
  <p class="formats">PNG, JPEG, WebP, GIF, BMP, or AVIF</p>

  <div class="samples" data-testid="samples">
    <span>or try a sample:</span>
    {#each SAMPLES as sample (sample.file)}
      <button
        type="button"
        class="sample"
        disabled={loadingSample !== ''}
        onclick={() => useSample(sample.file)}
        data-testid="sample-{sample.file.replace('.png', '')}"
      >
        {loadingSample === sample.file ? 'Loading…' : sample.label}
      </button>
    {/each}
  </div>
</div>

<style>
  .zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 3rem 2rem;
    text-align: center;
    background: var(--surface);
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius);
    transition: border-color 120ms ease, background 120ms ease;
  }

  .zone.dragging {
    border-color: var(--accent);
    background: var(--surface-raised);
  }

  .title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .sub,
  .formats {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.875rem;
  }

  .formats {
    font-size: 0.8rem;
  }

  .samples {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: var(--text-muted);
    font-size: 0.85rem;
  }

  .sample {
    margin: 0;
    padding: 0.3rem 0.7rem;
    background: var(--surface-raised);
    color: inherit;
    border: 1px solid var(--border-strong);
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 400;
  }

  .sample:hover:not(:disabled) {
    border-color: var(--accent);
    background: var(--surface-raised);
  }

  .sample:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  button {
    margin-top: 0.5rem;
    padding: 0.55rem 1.1rem;
    background: var(--accent);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-weight: 500;
  }

  button:hover {
    background: var(--accent-strong);
  }
</style>
